import { BbIcon } from '../../components/BbIcon'
import { SettingsSection, SettingsToggle } from './SettingsControls'
import { useSettingsForm } from './useSettingsForm'

/* Whether an import may conjure a record it cannot find.
 *
 * What it builds when it does — item type, which accounts to point at, the
 * account type given to a new account — lives on the Items tab. The two were
 * split because the defaults describe the tenant's chart of accounts rather
 * than this decision, and having them in both places meant two controls
 * writing one setting. */

/**
 * One switch that cannot take effect yet.
 *
 * A toggle can be on and still do nothing: the write path refuses to build an
 * Item with no income account, or an Inventory item with no asset account and
 * start date, because QuickBooks rejects those records. When that happens the
 * validation report says the name "was not found" — which reads as though the
 * switch were off. Nothing in the UI said otherwise, so the user had no way to
 * tell a broken premise from a broken feature.
 *
 * The label is not repeated here: this sits directly under the switch it is
 * about, and saying it twice cost the line the dialog needed to stay
 * scroll-free. The fix lives on another tab, so this both explains and goes
 * there.
 */
function BlockedToggle({ missing }: { missing: string }) {
  const { goToTab } = useSettingsForm()

  return (
    <div className="bb-opt-warn">
      <BbIcon name="warning" />
      <span>
        Won&rsquo;t take effect yet — {missing}.{' '}
        <button type="button" className="bb-linkbtn" onClick={() => goToTab('items')}>
          Fix on the Items tab
        </button>
      </span>
    </div>
  )
}

export function SettingsMappingTab() {
  const { draft } = useSettingsForm()

  /* Mirrors the server's own guards — TenantProjections.ItemDefaultsAreComplete
     and the CanCreate check in ImportValidator. Kept in step deliberately:
     a UI that says "ready" while the API says "not found" is worse than no
     warning at all. */
  const itemBlockedReason = (() => {
    if (!draft.autoCreateItems) return null
    if (!draft.defaultItemIncomeAccount) return 'no income account is set'
    if (draft.defaultItemType === 'Inventory' && !draft.defaultItemAssetAccount) {
      return 'inventory items need an asset account'
    }
    if (draft.defaultItemType === 'Inventory' && !draft.defaultItemInventoryStartDate) {
      return 'inventory items need an inventory start date'
    }
    return null
  })()

  const accountBlockedReason =
    draft.autoCreateAccounts && !draft.defaultAccountType ? 'no account type is set' : null

  return (
    <>
      <SettingsSection
        title="Auto-Create Records"
        help="When a name in your file does not exist in QuickBooks, choose whether to create it automatically or fail the row."
      >
        <SettingsToggle
          setting="autoCreateCustomers"
          label="Automatically create Customers"
          help="Create a new customer record when a name in your file is not found in QuickBooks."
        />
        <SettingsToggle
          setting="autoCreateVendors"
          label="Automatically create Vendors"
          help="Create a new vendor record when a name in your file is not found in QuickBooks."
        />
        <SettingsToggle
          setting="autoCreateItems"
          label="Automatically create Products & Services"
          help="Create a new item record when a name in your file is not found in QuickBooks."
        />
        {itemBlockedReason ? <BlockedToggle missing={itemBlockedReason} /> : null}

        <SettingsToggle
          setting="autoCreateAccounts"
          label="Automatically create Accounts"
          help="Create a new account record when a name in your file is not found in QuickBooks."
        />
        {accountBlockedReason ? <BlockedToggle missing={accountBlockedReason} /> : null}

        <SettingsToggle
          setting="autoCreateTaxAgencies"
          label="Automatically create Tax Agencies"
          help="Create a new tax agency record when a name in your file is not found in QuickBooks."
        />
      </SettingsSection>

      {/* Hidden while a toggle is blocked: the warning above already sends the
          user to the Items tab, and repeating it costs the height the dialog
          needs to stay scroll-free. */}
      {itemBlockedReason || accountBlockedReason ? null : (
        <p className="bb-map-help">
          What those records look like — item type, income and expense accounts, the type given to
          a new account — is on the <strong>Items</strong> tab.
        </p>
      )}
    </>
  )
}
